import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { useSelector } from "react-redux"
import { Fight, FightEvent, FightSegment, FullFight, SetupFightEvent } from "web/data/models"
import { EditEvent, EditSegment, OutlineEngine, TimeInterval } from 'web/engine/outline-engine'
import { assertDefined, assertInvariant, assumeDefined, expectOneById, removeFromArrayById } from "web/utils/utils"
import { RootState } from "../store"

export type EditFightState = {
    fight: Fight | undefined
    segments: FightSegment[]
    events: FightEvent[]

    setupFightEvents: SetupFightEvent[]
}

const initialState: EditFightState = {
    fight: undefined,
    segments: [],
    events: [],
    setupFightEvents: []
}

// #-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#
//
// Reducer
//
// #-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#-#

type BeginEditAction = {
    fullFight: FullFight

    // TODO: CRYPTIC AF. Currently this can be left empty when we
    // _know_ this editing session will NOT be able to move segments.
    // This should be made A LOT more obvious. Especially since we
    // also sometimes merge these events into the fullFight payload
    // and "pretend" they're outline events.
    //
    // This distinction needs to be made more clear or you're going to
    // foot-gun yourself.
    setupFightEvents: SetupFightEvent[]
}

const slice = createSlice({
    name: "edit-fight",
    initialState,
    reducers: {

        /**
         * Special side-channel for loading slice data directly from a
         * setup-recovery. Do not attempt to use for anything else.
         *
         * This is effectively a `preloadedState` call.
         */
        loadRecovery: (_, action: PayloadAction<{ recoveryJson: string }>) => {
            const jsonState = JSON.parse(action.payload.recoveryJson) as EditFightState

            return {
                ...jsonState
            }
        },

        // #-#-#-#-#-#-#-#-#-#-#-#-#-#-#
        // Being / end actions
        // #-#-#-#-#-#-#-#-#-#-#-#-#-#-#

        beginEdit: (state, action: PayloadAction<BeginEditAction>) => {
            const { fight, segments, events } = action.payload.fullFight
            const { setupFightEvents } = action.payload

            return {
                ...state,

                fight,
                segments,
                events,
                setupFightEvents
            }
        },

        endEdit: () => {
            return initialState
        },

        // #-#-#-#-#-#-#-#-#-#-#-#-#-#-#
        // Edit actions
        // #-#-#-#-#-#-#-#-#-#-#-#-#-#-#

        convertSetupFightEventToOutlineEvent: (state, action: PayloadAction<{ event: SetupFightEvent }>) => {

            const { event } = action.payload

            // TODO: The proper way to do this is obviously with some
            // `beginEdit:mode` flag...
            assertInvariant(
                state.setupFightEvents.length <= 1,
                'Expected setup fight events to be empty, this',
                'operation is only valid in the setup editing mode'
            )

            state.events = removeFromArrayById(state.events, [event])

            state.events.push({
                id: event.id,
                comment: event.comment,
                fightSegmentId: event.fightSegmentId,
                flags: event.flags,
                name: event.name,
                offset: event.offset,
                userCreated: event.userCreated
            })

        },

        appendNewSegment: (state, action: PayloadAction<{ segmentId: string }>) => {
            return {
                ...OutlineEngine
                    .fromState({
                        ...assumeDefined(state, 'fight')
                    })
                    .createSegment({ parentSegmentId: action.payload.segmentId })
                    .finish()
            }
        },

        createNewEvent: (state, action: PayloadAction<{ name: string; fightOffset: number; segmentId: string }>) => {
            return {
                ...OutlineEngine
                    .fromState({
                        ...assumeDefined(state, 'fight')
                    })
                    .createEvent({
                        userCreated: true,
                        ...action.payload
                    })
                    .finish()
            }
        },

        createNewSetupEvent: (state, action: PayloadAction<{ name: string; fightOffset: number; segmentId: string; setupId: string }>) => {
            return {
                ...OutlineEngine
                    .fromState({
                        ...assumeDefined(state, 'fight')
                    })
                    .createSetupEvent({
                        userCreated: true,
                        ...action.payload
                    })
                    .finish()
            }
        },

        deleteEvent: (state, action: PayloadAction<{ eventId: string }>) => {
            return {
                ...OutlineEngine
                    .fromState({
                        ...assumeDefined(state, 'fight')
                    })
                    .removeEvent(action.payload.eventId)
                    .finish()
            }
        },

        deleteSegment: (state, action: PayloadAction<{ segmentId: string }>) => {
            return {
                ...OutlineEngine
                    .fromState({
                        ...assumeDefined(state, 'fight')
                    })
                    .removeSegment(action.payload.segmentId)
                    .finish()
            }
        },

        updateSegment: (state, action: PayloadAction<EditSegment>) => {
            return {
                ...OutlineEngine
                    .fromState({
                        ...assumeDefined(state, 'fight')
                    })
                    .editSegment({ ...action.payload })
                    .finish()
            }
        },

        updateEvent: (state, action: PayloadAction<{ event: EditEvent; opt: { bulkEdit: boolean } }>) => {

            const oldEvent = {
                ...expectOneById(state.events, action.payload.event.eventId)
            }

            const newState = {
                ...OutlineEngine
                    .fromState({
                        ...assumeDefined(state, 'fight')
                    })
                    .editEvent({ ...action.payload.event })
                    .finish()
            }

            if (action.payload.opt.bulkEdit && action.payload.event.name != null) {
                newState.events.forEach(ev => {
                    assertDefined(action.payload.event.name)

                    if (ev.name === oldEvent.name) {
                        ev.name = action.payload.event.name
                    }
                })
            }

            return newState
        },

    }
})

function selectors(state: EditFightState) {
    return {
        getSegment: (segmentId: string) => {
            return expectOneById(state.segments, segmentId)
        },

        getValidSegmentInterval: (
            segment: FightSegment, params?: { includeMaxOffset?: boolean }
        ): TimeInterval | undefined => {

            return OutlineEngine.utils.getValidSegmentInterval({
                segment,
                allSegments: state.segments,
                allEvents: state.events,
                includeMaxOffset: params?.includeMaxOffset
            })
        },

        getChildSegmentOffset: (segment: FightSegment) => {

            if (segment.childId != null) {
                return expectOneById(state.segments, segment.childId).offset
            }

            return undefined
        }
    }
}

export const EditFightActions = slice.actions
export const EditFightReducer = slice.reducer
export const EditFightSelectors = selectors
export const useEditFightState = () => useSelector<RootState, EditFightState>(state => state.editFight)

type ReturnHook = [
    ReturnType<typeof selectors>,
    typeof slice.actions,
    EditFightState
]

/**
 * @note This function has a horrible name and is easily confused with
 * `useEditFightState`. Why did you create this and what's its
 * purpose? -- It looks like an attempt to get a selector wrapper?
 */
export function useEditFight_BadName(): ReturnHook {
    const state = useSelector<RootState, EditFightState>(state => state.editFight)

    return [selectors(state), slice.actions, state]
}
